"use no memo";

import { Button, Tooltip } from "@stacklet/ui";
import { type UseFormReturn } from "react-hook-form";

import { type GroupFormSchema, getDefaultGroupValues } from "./GroupSchema";

interface Props {
  /** Whether this is an edit form (true) or add form (false) */
  isEdit: boolean;
  /** Whether the form is currently submitting */
  isSubmitting: boolean;
  /** Whether the user has permission to manage groups */
  canManageGroups: boolean;
  /** Callback for cancel button */
  onCancel: () => void;
  /** Callback for the Reset button; defaults to resetting to blank values */
  onReset?: () => void;
  /** Form instance from react-hook-form */
  form: UseFormReturn<GroupFormSchema>;
}

export function GroupActionButtons({
  isEdit,
  isSubmitting,
  canManageGroups,
  onCancel,
  onReset,
  form,
}: Props) {
  const {
    formState: { isValid, isDirty },
    reset,
  } = form;

  // Save stays available on a pristine edit form: membership changes commit on
  // their own, so a page whose only changes were to membership still needs a
  // Save to close it. Submitting an unchanged form runs no mutations — the edit
  // form returns to the details page instead. Reset still needs a dirty form to
  // have anything to undo.
  const isSaveDisabled = isSubmitting || !isValid || !canManageGroups;

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else {
      reset(getDefaultGroupValues());
    }
  };

  return (
    <>
      <Button
        isDisabled={isEdit ? !canManageGroups : false}
        onPress={onCancel}
        variant="tertiary"
      >
        Cancel
      </Button>
      <Button isDisabled={!isDirty} onPress={handleReset} variant="tertiary">
        Reset
      </Button>
      <Tooltip
        title={
          canManageGroups
            ? ""
            : `You need system.write permission to ${isEdit ? "edit" : "add"} groups`
        }
        asChild
      >
        <span>
          <Button
            isDisabled={isSaveDisabled}
            isPending={isSubmitting}
            pendingText={`${isEdit ? "Updating" : "Adding"} Group…`}
            type="submit"
            variant="primary"
          >
            Save
          </Button>
        </span>
      </Tooltip>
    </>
  );
}
